import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";

const navItems = [
  { label: "首页", to: "/" },
  { label: "求番推荐", to: "/recommend" },
  { label: "避雷指南", to: "/avoid" },
  { label: "补番顺序", to: "/order" },
  { label: "漫评讨论", to: "/review" },
  { label: "关于我们", to: "/about" },
];

interface NavbarProps {
  transparent?: boolean;
}

export default function Navbar({ transparent = false }: NavbarProps) {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [query, setQuery] = useState("");
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname]);

  const solid = scrolled || !transparent;
  const isActive = (to: string) =>
    to === "/" ? location.pathname === "/" : location.pathname.startsWith(to);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (query.trim()) {
      navigate(`/recommend?q=${encodeURIComponent(query.trim())}`);
    }
  };

  return (
    <header
      className={`fixed inset-x-0 top-0 z-50 transition-all duration-300 ${
        solid
          ? "border-b border-background-200 bg-background-50/95 backdrop-blur"
          : "border-b border-transparent bg-transparent"
      }`}
    >
      <nav className="container-x flex h-16 items-center justify-between gap-4 md:h-[72px]">
        <Link to="/" className="flex items-center gap-2.5" aria-label="新御宅屋首页">
          <span
            className={`flex h-9 w-9 items-center justify-center rounded-xl text-lg ${
              solid ? "bg-primary-500 text-background-50" : "bg-background-50/20 text-background-50 backdrop-blur"
            }`}
          >
            <i className="ri-ancient-pavilion-line"></i>
          </span>
          <span
            className={`font-heading text-xl tracking-wide ${
              solid ? "text-primary-800" : "text-background-50"
            }`}
          >
            新御宅屋
          </span>
        </Link>

        <div className="hidden items-center gap-1 md:flex">
          {navItems.map((item) => (
            <Link
              key={item.to}
              to={item.to}
              className={`rounded-full px-4 py-2 text-sm font-bold transition-colors duration-200 ${
                isActive(item.to)
                  ? solid
                    ? "bg-primary-100 text-primary-800"
                    : "bg-background-50/20 text-background-50"
                  : solid
                    ? "text-foreground-700 hover:bg-background-200 hover:text-primary-700"
                    : "text-background-50/85 hover:bg-background-50/20 hover:text-background-50"
              }`}
            >
              {item.label}
            </Link>
          ))}
        </div>

        <div className="flex items-center gap-2">
          <form
            onSubmit={handleSearch}
            className={`hidden items-center gap-2 rounded-full border px-3 py-2 lg:flex ${
              solid ? "border-background-300 bg-background-100" : "border-background-50/30 bg-background-50/15 backdrop-blur"
            }`}
          >
            <i className={`ri-search-line text-sm ${solid ? "text-foreground-600" : "text-background-50/80"}`}></i>
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="搜番名 / 关键词"
              className={`w-36 bg-transparent text-sm outline-none placeholder:font-normal ${
                solid ? "text-foreground-800 placeholder:text-foreground-500" : "text-background-50 placeholder:text-background-50/60"
              }`}
              aria-label="搜索番剧"
            />
          </form>

          <Link
            to="/contact"
            className={`hidden whitespace-nowrap rounded-full px-4 py-2 text-sm font-bold transition-colors duration-200 sm:inline-flex ${
              solid
                ? "bg-primary-500 text-background-50 hover:bg-primary-600"
                : "bg-background-50/20 text-background-50 backdrop-blur hover:bg-background-50/30"
            }`}
          >
            投稿 / 联系
          </Link>

          <button
            type="button"
            onClick={() => setMenuOpen((v) => !v)}
            aria-label="打开菜单"
            aria-expanded={menuOpen}
            className={`flex h-10 w-10 items-center justify-center rounded-full text-xl md:hidden ${
              solid ? "bg-background-100 text-foreground-800" : "bg-background-50/20 text-background-50"
            }`}
          >
            <i className={menuOpen ? "ri-close-line" : "ri-menu-3-line"}></i>
          </button>
        </div>
      </nav>

      {menuOpen && (
        <div className="border-t border-background-200 bg-background-50 md:hidden">
          <div className="container-x flex flex-col gap-1 py-4">
            {navItems.map((item) => (
              <Link
                key={item.to}
                to={item.to}
                className={`rounded-xl px-4 py-3 text-sm font-bold ${
                  isActive(item.to)
                    ? "bg-primary-100 text-primary-800"
                    : "text-foreground-700 hover:bg-background-200"
                }`}
              >
                {item.label}
              </Link>
            ))}
            <Link
              to="/contact"
              className="mt-2 rounded-full bg-primary-500 px-4 py-3 text-center text-sm font-bold text-background-50"
            >
              投稿 / 联系
            </Link>
          </div>
        </div>
      )}
    </header>
  );
}